/**
 * useLoadDetailsExport.ts
 * Builds and downloads a full .xlsx export of the ASN Gap Load Details
 * table. Reuses the same workbook-write + browser-download plumbing as the
 * KPI One Pager V2 export (`downloadWorkbook`, from the Analytics Hub
 * export utilities) — but because `POST /api/asn/load-details/list` is
 * paginated, this hook first fetches *every* page for the currently applied
 * filters/sort/search (via `fetchAsnLoadDetailsAllPages`) and combines all
 * rows before building the workbook, rather than exporting only the
 * currently-visible page.
 *
 * Required flow:
 *   Click Export → call page 1 → read totalPages → fetch remaining pages →
 *   combine all rows → map them via mapApiRowToTableRow → generate Excel →
 *   download the file.
 */

import { useCallback, useState } from 'react';
import { ASNLoadDetailsRequest, fetchAsnLoadDetailsAllPages } from '../api/asnOverviewKpiApi';
import { buildLoadDetailsWorkbook } from '../utils/loadDetailsWorkbookBuilder';
import { downloadWorkbook, WorkbookDownloadError } from '../../../../AnalyticsHub/utils/workbookDownload';
import { useToast } from '../../../../../components/LhToastContainer';

export interface UseLoadDetailsExportOptions {
  /** Page size used while paging through every row for export — kept
   * independent of (and larger than) the table's on-screen page size, to
   * minimize the number of round trips needed to fetch the full row set. */
  defaultPageSize?: number;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** e.g. "ASN_Load_Details_2026-08-27_143205.xlsx" */
export const buildLoadDetailsExportFileName = (now: Date): string => {
  const yyyy = now.getFullYear();
  const mm = pad2(now.getMonth() + 1);
  const dd = pad2(now.getDate());
  const hh = pad2(now.getHours());
  const min = pad2(now.getMinutes());
  const ss = pad2(now.getSeconds());
  return `ASN_Load_Details_${yyyy}-${mm}-${dd}_${hh}${min}${ss}.xlsx`;
};

export interface UseLoadDetailsExportReturn {
  readonly isExporting: boolean;
  /**
   * Kicks off the export using `appliedRequest` (the currently applied
   * filters/sort/search — e.g. `state.asnLoadDetails.lastRequest`) as the
   * base request; `pageNumber`/`pageSize` on it are overridden internally
   * so every page gets fetched regardless of the table's current on-screen
   * page/page-size.
   */
  readonly exportLoadDetails: (appliedRequest: ASNLoadDetailsRequest) => Promise<void>;
}

export const useLoadDetailsExport = (
  options: UseLoadDetailsExportOptions = {},
): UseLoadDetailsExportReturn => {
  const { defaultPageSize = 200 } = options;
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const exportLoadDetails = useCallback(
    async (appliedRequest: ASNLoadDetailsRequest) => {
      // Prevent duplicate/overlapping export requests (e.g. rapid double-click).
      if (isExporting) return;
      setIsExporting(true);

      // Freeze the request as of the click — the currently *applied*
      // filters/sort/search (never a not-yet-applied dropdown edit made
      // while the export is in flight). Table pagination is irrelevant to
      // export and is overridden below.
      const exportRequest: ASNLoadDetailsRequest = {
        ...appliedRequest,
        pageNumber: 1,
        pageSize: defaultPageSize,
      };

      try {
        let combined;
        try {
          combined = await fetchAsnLoadDetailsAllPages(exportRequest);
        } catch (error: any) {
          const msg = error?.response?.data?.message
            || error?.message
            || 'Unable to export the Load Details table. Please try again.';
          toast.show(msg, 'error', 'Export Failed');
          return;
        }

        if (!combined.rows || combined.rows.length === 0) {
          // Never download an empty file — surface the backend's own
          // message verbatim (matches the table's own empty-state contract).
          toast.show(combined.message || 'No data available for export.', 'warning', 'Export');
          return;
        }

        let workbook;
        try {
          workbook = buildLoadDetailsWorkbook(combined.rows);
        } catch (error: any) {
          toast.show(
            error?.message || 'Failed to build the export file. Please try again.',
            'error',
            'Export Failed',
          );
          return;
        }

        const fileName = buildLoadDetailsExportFileName(new Date());
        try {
          await downloadWorkbook(workbook, fileName);
        } catch (error: any) {
          const msg = error instanceof WorkbookDownloadError
            ? error.message
            : (error?.message || 'The download was blocked by the browser. Please allow downloads and try again.');
          toast.show(msg, 'error', 'Export Failed');
          return;
        }

        toast.show(
          `Exported ${combined.rows.length} row${combined.rows.length === 1 ? '' : 's'} to ${fileName}`,
          'success',
          'Export Complete',
        );
      } finally {
        setIsExporting(false);
      }
    },
    [isExporting, defaultPageSize, toast],
  );

  return { isExporting, exportLoadDetails };
};

export default useLoadDetailsExport;
